import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useAppBranding } from '../../config/branding';
import { PortalType } from '../../config/enum';
import { detectPortalType } from '../../contexts/PortalContext/PortalProvider';
import CancelReservation from '../../ocpp-message-pages/cancel-reservation/CancelReservation';
import ChangeConfiguration from '../../ocpp-message-pages/change-configuration/ChangeConfiguration';
import ClearCache from '../../ocpp-message-pages/clear-cache/ClearCache';
import GetConfiguration from '../../ocpp-message-pages/get-configuration/GetConfiguration';
import GetDiagnostics from '../../ocpp-message-pages/get-diagnostics/GetDiagnostics';
import GetLocalVersionList from '../../ocpp-message-pages/get-local-list-version/GetLocalListVersion';
import OcppConsole from '../../ocpp-message-pages/ocpp-console/OcppConsole';
import RemoteStartTransaction from '../../ocpp-message-pages/remote-start-charging/RemoteStartTransaction';
import RemoteStopTransaction from '../../ocpp-message-pages/remote-stop-charging/RemoteStopTransaction';
import ReserveNow from '../../ocpp-message-pages/reserve-now/ReserveNow';
import Reset from '../../ocpp-message-pages/reset/Reset';
import TriggerMessage from '../../ocpp-message-pages/trigger-message/TriggerMessage';
import UnlockConnector from '../../ocpp-message-pages/unlock-connector/UnlockConnector';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import Footer from '../../shared-components/footer/Footer';
import Header from '../../shared-components/header/Header';
import GlobalTableModal from '../../shared-components/modal-box/GlobalTableModal';
import OCPPMessageSidebar from '../../shared-components/ocpp-message/sidebar/Sidebar';
import Sidebar from '../../shared-components/sidebar/Sidebar';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
import AccountManagement from '../account-management';
import AccountsManagementPayout from '../account-management/accounts-management-payout';
import UtilityProvider from '../account-management/utilityProvider';
import Brand from '../brand';
import CampaignManagement from '../campaign';
import CreateCampaign from '../campaign/components/CreateCampaign';
import ViewCampaign from '../campaign/components/ViewCampaign';
import DeviceManagement from '../charger-pages/advanced-charger-management/DeviceManagement';
import DeviceManagementPartner from '../charger-pages/charger-management';
import Connector from '../connector';
import ConnectorRating from '../connector-rating';
import Dashboard from '../dashboards/network';
import CreateNewDashboard from '../dashboards/network/components/CreateNewDashboard';
import CustomizeDashboard from '../dashboards/network/components/CustomizeDashboard';
import PartnerDashboard from '../dashboards/partner';
import QRCodeInputFields from '../device-qr-customization/components/QrCodeInputField';
import DynamicPricing from '../dynamic-pricing';
import EmspInvoice from '../emsp-invoice';
import EMSPPayouts from '../emsp-payouts';
import EMSPTransactionManagement from '../emsp-transactions';
import EndConsumerManagementV2 from '../end-consumer-management-v2';
import EndConsumersProfile from '../end-consumer-management-v2/components/EndConsumerProfile';
import EventDeviceHub from '../ev-device-hub';
import AddDevice from '../ev-device-hub/components/AddDevice';
import DeviceHubDetailsContainer from '../ev-device-hub/components/device-details/DeviceHubDetailsContainer';
import DeviceHubDashboard from '../ev-device-hub/components/DeviceHubDashboard';
import FacilityTag from '../facility-tag/index';
import FeeConfiguration from '../fee-configuration';
import FirmwareVersion from '../firmware-version';
import GuestCharging from '../guest-charging';
import AddGuestCharging from '../guest-charging/components/AddGuestCharging';
import EditGuestCharging from '../guest-charging/components/EditGuestCharging';
import LocationManagementSidePanel from '../location-pages/advanced-location-management';
import LocationManagement from '../location-pages/location-management';
import LoginV3 from '../login-v3/components/Login';
import AuthLayout from '../login-v3/index';
import ScheduleMaintenance from '../maintenance-scheduling/index';
import Model from '../model';
import ConnectionManagement from '../ocpi-managment/components/ConnectionManagement';
import OCPIRoleOperations from '../ocpi-managment/components/OCPIRoleOperations';
import OCPIManagement from '../ocpi-managment/index';
import OCPPLogs from '../ocpp-logs';
import PartnerPayouts from '../partner-payouts';
import PaymentGateway from '../payment-gateway';
import PaymentTransactions from '../payment-gateway/components/PaymentTransactions';
import CPOTransactionManagement from '../payments';
import EMSPPaymentManagement from '../payments/emsp-payments';
import PenaltyManagement from '../penalty-management';
import PersonalDeviceTable from '../personal-device/PersonalDeviceTable';
import PowerBankRequest from '../power-Bank-payment';
import PrivacyPolicy from '../privacy-policy';
import Profile from '../profile';
import ReportManagement from '../reports';
import RevenueManagement from '../revenue-management';
import RFIDManagement from '../rfid-management';
import EMSPChargingSessions from '../session/emsp-sessions';
import CPOChargingSessions from '../session/network-session';
import PartnerChargingSessions from '../session/partner-sessions';
import Settings from '../settings/Settings';
import CPOPayouts from '../stripe-payouts';
import Tariff from '../tariffs';
import AddTariff from '../tariffs/components/Add-tariff';
import CloneTariff from '../tariffs/components/Clone-tariff';
import EditTariff from '../tariffs/components/Edit-tariff';
import TariffCalculator from '../tariffs/components/tariff-cal';
import ViewTariff from '../tariffs/components/View-tariff';
import UserManagement from '../user-management';
import AddRolePage from '../user-management/components/AddRolePage';
import EditRolePage from '../user-management/components/EditRolePage';
import VlbcManagement from '../vlbc-management';

import AuthGuard from './components/AuthGuard';

/**
 * Wrapper Route function component
 */
function WrapperRoute() {
  const { i18n } = useTranslation();
  const { meta: brandMeta } = useAppBranding();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(true);
  const { open } = useAppSelector((state) => state.tableModal);
  const hideHeader = location.pathname.includes('dashboard/customize-dashboard');
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const { type } = useParams<{ type: string }>();

  useEffect(() => {
    if (
      location.pathname.includes(`end-consumer/${type}/profile/${userCustomId}/station`) ||
      location.pathname.includes(`end-consumer/${type}/profile/${userCustomId}/location`) ||
      location.pathname.includes(`end-consumer/${type}/profile/${userCustomId}/device-expanded`) ||
      location.pathname.includes('device-management/device-expanded') ||
      location.pathname.includes('charging-station/approved/station-expanded') ||
      location.pathname.includes('charging-location/approved/location-expanded') ||
      location.pathname.includes('charging-station/unapproved/station-expanded') ||
      location.pathname.includes('charging-location/unapproved/location-expanded') ||
      location.pathname.includes('charging-station/rejected/station-expanded') ||
      location.pathname.includes('charging-location/rejected/location-expanded') ||
      location.pathname.includes('device-management/station-expanded') ||
      location.pathname.includes('device-management/location-expanded') ||
      location.pathname.includes('/station-expanded/') ||
      location.pathname.includes('/location-expanded/') ||
      location.pathname.includes('/charger-expanded/') ||
      location.pathname.includes('transaction-management/station') ||
      location.pathname.includes('transaction-management/location')
    ) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
  }, [location.pathname]);
  // Search Params
  const [, setSearchParameters] = useSearchParams();

  useEffect(() => {
    const staleSidePanelKeys = [
      'deviceId',
      'stationId',
      'locationId',
      'accountCustomId',
      'sessionId',
      'cdrObjectId',
      'personalChargerId',
      'userCustomId',
      'connectionId',
    ];

    /**
     * Strips stale side-panel query params (e.g. connectionId) left over from a page that
     * was navigated away from. Reads the live search params via the updater form rather than
     * a closed-over value, since this also needs to run from the pageshow handler below.
     * @param {any} previous - The current search params, supplied by setSearchParameters.
     * @returns {any} The search params with stale keys removed.
     */
    const clearStaleSidePanelParameters = (previous: URLSearchParams) => {
      const parameters = new URLSearchParams(previous);
      staleSidePanelKeys.forEach((key) => parameters.delete(key));
      return parameters;
    };

    setSearchParameters(clearStaleSidePanelParameters, { replace: true });

    /**
     * Re-runs the same cleanup when the page is restored from the browser's back/forward
     * cache (bfcache). Firefox and Safari restore bfcached pages without re-mounting React or
     * re-running this effect, so a stale connectionId (etc.) left in the address bar from
     * before navigating away otherwise only gets cleared in browsers that happen to force a
     * fresh mount on back-navigation (e.g. Chrome when bfcache is ineligible). `pageshow` with
     * `event.persisted` fires consistently across browsers for bfcache restores.
     * @param {any} event - The pageshow event.
     */
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setSearchParameters(clearStaleSidePanelParameters, { replace: true });
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  return showSidebar ? (
    <div className={`ev-layout-wrap ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="ev-sidebar">
        <Sidebar />
      </div>
      <div className="ev-content-wrap" style={{ display: !hideHeader ? 'grid' : 'block' }}>
        {!hideHeader && <Header />}
        <div className="ev-content">
          <Outlet />
          {open && <GlobalTableModal />}
        </div>
        {brandMeta.footerText && <Footer />}
      </div>
    </div>
  ) : (
    <div className={`ev-layout-wrap hide-sidebar ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="ev-content-wrap" style={{ display: !hideHeader ? 'grid' : 'block' }}>
        {!hideHeader && <Header />}
        <div className="ev-content">
          <Outlet />
        </div>
        {brandMeta.footerText && <Footer />}
      </div>
    </div>
  );
}

/**
 * Function to handle OCPP message commands
 * @returns {any} The wrapped OCPP route components.
 */
function OcppWrapperRoute() {
  return (
    <div className="ev-layout-wrap">
      <div className="ev-sidebar">
        <OCPPMessageSidebar />
      </div>
      <div className="ocpp-message-content-wrap">
        <div className="ocpp-message-conetnt">
          <Outlet />
        </div>
        <div className="ocpp-console">
          <OcppConsole />
        </div>
      </div>
    </div>
  );
}

/**
 * Router page function component
 */
function Router() {
  const dispatch = useAppDispatch();
  const portalType = detectPortalType();

  const navigate = useNavigate();
  const { notificationMessage, notificationType, notificationStatus, notificationCode } =
    useAppSelector((state) => state.notification);
  const routes = [
    {
      path: 'dashboard',
      children: [
        {
          id: 1,
          path: '',
          element: portalType === PortalType.PARTNER ? <PartnerDashboard /> : <Dashboard />,
          permission: ['cpoDashboard', 'partnerDashboardView'],
        },
        {
          id: 2,
          path: 'customize-dashboard',
          element: <CustomizeDashboard />,
          permission: ['cpoCustomizeDashboardView'],
        },
      ],
    },
    {
      path: 'accounts/partners',
      children: [
        {
          id: 1,
          path: '',
          element: <AccountManagement />,
          permission: ['cpoPartnerView'],
        },
        {
          id: 2,
          path: 'payout-management',
          element: <AccountsManagementPayout />,
          permission: ['cpoPartnerView'],
        },
        {
          id: 99,
          path: 'details/payout-management/:id',
          element: <AccountsManagementPayout />,
          permission: ['cpoPartnerView'],
        },
      ],
    },
    {
      path: 'accounts/utility-provider',
      children: [
        {
          id: 1,
          path: '',
          element: <UtilityProvider />,
          permission: ['cpoUtilityProviderView'],
        },
        {
          id: 2,
          path: 'payout-management',
          element: <AccountsManagementPayout />,
          permission: ['cpoUtilityProviderView'],
        },
      ],
    },
    {
      path: 'operations/charging-station',
      children: [
        {
          id: 1,
          path: '',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 3,
          path: ':page',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 6,
          path: ':page/station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 7,
          path: ':page/location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
      ],
    },
    {
      path: 'operations/charging-location',
      children: [
        {
          id: 1,
          path: '',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 3,
          path: ':page',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 6,
          path: ':page/location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 7,
          path: ':page/station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
      ],
    },
    {
      path: 'reports',
      children: [
        {
          id: 4,
          path: ':page',
          element: <ReportManagement />,
          permission: [
            'cpoEnergyReportView',
            'cpoRevenueReportView',
            'cpoBookingReportView',
            'cpoChargerUtilizationReportView',
            'cpoPerformanceReportView',
            'cpoMaintenanceReportView',
            'reporting_tools',
            'mobilityEnergyReportView',
            'mobilityRevenueReportView',
          ],
        },
      ],
    },
    {
      path: 'user-management',
      children: [
        {
          id: 1,
          path: ':currentPage',
          element: <UserManagement />,
          permission: ['userManagementView'],
        },
        {
          id: 2,
          path: ':currentPage/add',
          element: <AddRolePage />,
          permission: ['userManagementAdd'],
        },
        {
          id: 3,
          path: ':currentPage/edit/:id',
          element: <EditRolePage />,
          permission: ['userManagementUpdate'],
        },
      ],
    },
    {
      path: 'end-consumer',
      children: [
        {
          id: 6,
          path: '',
          element: <EndConsumerManagementV2 />,
          permission: ['mobilityEndConsumerView'],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId',
          element: <EndConsumersProfile />,
          permission: ['mobilityEndConsumerView'],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId/station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['mobilityEndConsumerView'],
        },
        {
          id: 66,
          path: ':type/profile/:userCustomId/location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['mobilityEndConsumerView'],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId/device/:id/:tab',
          element: <DeviceManagement />,
          permission: ['mobilityEndConsumerView'],
        },
        {
          id: 7,
          path: ':type/profile/:userCustomId/device-expanded/:id/:tab',
          element: <DeviceManagement />,
          permission: ['mobilityEndConsumerView'],
        },
      ],
    },
    {
      path: 'revenue/',
      children: [
        {
          id: 7,
          path: 'partner-transactions',
          element: <CPOTransactionManagement />,
          permission: ['partnerTransactionView'],
        },
        {
          id: 7,
          path: 'cpo-transactions',
          element: <CPOTransactionManagement />,
          permission: ['cpoTransactionView'],
        },
        {
          id: 7,
          path: 'emsp-transactions',
          element: <EMSPTransactionManagement />,
          permission: ['mobilityTransactionsView'],
        },
        {
          id: 7,
          path: 'invoice',
          element: <EmspInvoice />,
          permission: ['mobilityPayoutsView'],
        },
        {
          id: 8,
          path: 'emsp-payments',
          element: <EMSPPaymentManagement />,
          permission: ['mobilityTransactionsView'],
        },
        {
          id: 9,
          path: 'power-bank-payment',
          element: <PowerBankRequest />,
          permission: ['mobilityPayoutsView'],
        },
      ],
    },
    {
      path: 'operations/',
      children: [
        {
          id: 11,
          path: 'partner-sessions',
          element: <PartnerChargingSessions />,
          permission: ['partnerSessionView'],
        },
        {
          id: 11,
          path: 'cpo-sessions',
          element: <CPOChargingSessions />,
          permission: ['cpoSessionView'],
        },
        {
          id: 11,
          path: 'emsp-sessions',
          element: <EMSPChargingSessions />,
          permission: ['mobilitySessionsView'],
        },
        {
          id: 4,
          path: 'rfid',
          element: <RFIDManagement />,
          permission: ['mobilitySessionsView'],
        },
      ],
    },
    {
      path: 'configuration/revenue-management',
      children: [
        {
          id: 88,
          path: ':currentPage',
          element: <RevenueManagement />,
          permission: ['cpoRevenueReportView'],
        },
        {
          id: 8,
          path: '',
          element: <RevenueManagement />,
          permission: ['cpoRevenueReportView'],
        },
      ],
    },
    {
      path: 'revenue/revenue-sharing',
      children: [
        {
          id: 888,
          path: ':currentPage',
          element: <RevenueManagement />,
          permission: ['cpoRevenueView'],
        },
        {
          id: 8,
          path: '',
          element: <RevenueManagement />,
          permission: ['cpoRevenueView'],
        },
      ],
    },
    {
      path: 'configuration/dynamic-pricing-approval/:currentPage',
      children: [
        {
          id: 9,
          path: '',
          element: <DynamicPricing />,
          permission: ['dynamic_pricing'],
        },
      ],
    },
    {
      path: 'tools/qr-code-generator',
      children: [
        {
          id: 10,
          path: '',
          element: <QRCodeInputFields />,
          permission: ['cpoQrCodeGenerationView', 'partnerQrCodeView'],
        },
      ],
    },
    {
      path: 'operations/chargers',
      children: [
        {
          id: 1,
          path: '',
          element: <DeviceManagementPartner />,
          permission: ['cpoChargingPointView', 'view_device', 'partnerChargingDevicesView'],
        },
        {
          id: 2,
          path: ':page',
          element: <DeviceManagementPartner />,
          permission: ['cpoChargingPointView', 'partnerChargingDevicesView'],
        },
        {
          id: 2,
          path: 'device/:id/:tab',
          element: <DeviceManagement />,
          permission: ['cpoChargingPointView', 'partnerChargingDevicesView'],
        },
        {
          id: 3,
          path: 'device-expanded/:id/:tab',
          element: <DeviceManagement />,
          permission: ['cpoChargingPointView', 'partnerChargingDevicesView'],
        },
        {
          id: 5,
          path: 'location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 55,
          path: 'station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 6,
          path: 'location-expanded/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 66,
          path: 'station-expanded/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
      ],
    },
    {
      path: 'operations/personal-charger',
      children: [
        {
          id: 1,
          path: '',
          element: <PersonalDeviceTable />,
          permission: ['mobilityChargersView'],
        },
      ],
    },
    {
      path: 'ev-device-hub',
      children: [
        {
          id: 1,
          path: 'dashboard',
          element: <DeviceHubDashboard />,
          permission: ['devicesDashboard'],
        },
        {
          id: 2,
          path: 'devices',
          element: <EventDeviceHub />,
          permission: ['devicesEvDeviceHubView'],
        },
        {
          id: 3,
          path: 'add-device',
          element: <AddDevice />,
          permission: ['devicesEvDeviceHubAdd'],
        },
        {
          id: 4,
          path: 'edit-device/:id',
          element: <AddDevice />,
          permission: ['devicesEvDeviceHubUpdate'],
        },
        {
          id: 5,
          path: 'device-details/:id',
          element: <DeviceHubDetailsContainer />,
          permission: ['devicesEvDeviceHubView'],
        },
        {
          id: 6,
          path: 'device-details/:id/:tab',
          element: <DeviceHubDetailsContainer />,
          permission: ['devicesEvDeviceHubView'],
        },
        {
          id: 7,
          path: 'connector-rating',
          element: <Connector />,
          permission: ['devicesRatingView'],
        },
        {
          id: 8,
          path: 'models',
          element: <Model />,
          permission: ['devicesModelsView'],
        },
        {
          id: 9,
          path: 'brands',
          element: <Brand />,
          permission: ['devicesBrandsView'],
        },
        {
          id: 10,
          path: 'firmware-versions',
          element: <FirmwareVersion />,
          // TEMP: reusing devicesBrandsView until firmwareArtifactsView is provisioned on the backend
          permission: ['devicesBrandsView'],
        },
      ],
    },

    {
      path: 'tools/campaign',
      children: [
        {
          id: 1,
          path: '',
          element: <CampaignManagement />,
          permission: ['cpoCampaignManagementView'],
        },
        {
          id: 2,
          path: 'create',
          element: <CreateCampaign />,
          permission: ['cpoCampaignManagementView'],
        },
        {
          id: 3,
          path: 'view/:campaignId',
          element: <ViewCampaign />,
          permission: ['cpoCampaignManagementView'],
        },
      ],
    },
    {
      path: 'revenue/cpo-payouts/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <CPOPayouts />,
          permission: ['cpoCpoPayoutsView'],
        },
      ],
    },
    {
      path: 'revenue/emsp-payouts/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <EMSPPayouts />,
          permission: ['mobilityPayoutsView'],
        },
      ],
    },
    {
      path: 'revenue/partner-payouts/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <PartnerPayouts />,
          permission: [],
        },
      ],
    },

    {
      path: 'configuration/fee-configuration',
      children: [
        {
          id: 1,
          path: '',
          element: <FeeConfiguration />,
          permission: ['mobilityFeeConfigurationView'],
        },
      ],
    },
    {
      path: 'penalty-management/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <PenaltyManagement />,
          permission: [],
        },
      ],
    },
    {
      path: 'connector-rating',
      children: [
        {
          id: 1,
          path: '',
          element: <ConnectorRating />,
          permission: ['devicesRatingView'],
        },
      ],
    },
    {
      path: 'tariffs',
      children: [
        {
          id: 1,
          path: '',
          element: <Tariff />,
          permission: ['cpoTariffView', 'partnerTariffView'],
        },
        {
          id: 2,
          path: 'add-tariff',
          element: <AddTariff />,
          permission: ['cpoTariffAdd', 'partnerTariffAdd'],
        },
        {
          id: 3,
          path: 'view/:id',
          element: <ViewTariff />,
          permission: ['cpoTariffView', 'partnerTariffView'],
        },
        {
          id: 4,
          path: 'edit-tariff/:tariffId',
          element: <EditTariff />,
          permission: ['cpoTariffUpdate', 'partnerTariffUpdate'],
        },
        {
          id: 4,
          path: 'clone-tariff/:tariffId',
          element: <CloneTariff />,
          permission: ['cpoTariffUpdate', 'partnerTariffUpdate'],
        },
        {
          id: 5,
          path: 'calculator',
          element: <TariffCalculator />,
          permission: ['cpoTariffCalculation', 'partnerTariffCalculation'],
        },
      ],
    },
    {
      path: 'tools/calculator',
      children: [
        {
          id: 5,
          path: '',
          element: <TariffCalculator allowType />,
          permission: ['cpoTariffCalculation', 'partnerTariffCalculation'],
        },
      ],
    },
    {
      path: 'tools/vlbc',
      children: [
        {
          id: 1,
          path: '',
          element: <VlbcManagement />,
          permission: [],
        },
      ],
    },
    {
      path: 'tools/maintenance-scheduling',
      children: [
        {
          id: 1,
          path: '',
          element: <ScheduleMaintenance />,
          permission: [],
        },
        {
          id: 3,
          path: 'station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
        {
          id: 4,
          path: 'location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'profile',
      children: [
        {
          id: 1,
          path: '',
          element: <Profile />,
          permission: [],
        },
      ],
    },
    {
      path: 'operations/facility-management',
      children: [
        {
          id: 1,
          path: '',
          element: <FacilityTag />,
          permission: [],
        },
        {
          id: 2,
          path: 'station/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 3,
          path: 'location/:id/:tab',
          element: <LocationManagementSidePanel />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
      ],
    },
    {
      path: 'operations/guest-charging',
      children: [
        {
          id: 1,
          path: '',
          element: <GuestCharging />,
          permission: ['mobilityGuestUserView'],
        },
        {
          id: 2,
          path: 'add',
          element: <AddGuestCharging />,
          permission: ['mobilityGuestUserAdd'],
        },
        {
          id: 2,
          path: ':id',
          element: <EditGuestCharging />,
          permission: ['mobilityGuestUserUpdate'],
        },
      ],
    },
    {
      path: 'configuration/PaymentGateway',
      children: [
        {
          id: 1,
          path: '',
          element: <PaymentGateway />,
          permission: ['mobilityPaymentGatewayView', 'cpoPaymentGatewayView'],
        },
        {
          id: 2,
          path: 'transactions',
          element: <PaymentTransactions />,
          permission: ['mobilityPaymentTransactionView', 'cpoPaymentTransactionView'],
        },
      ],
    },
    {
      path: 'location-management',
      children: [
        {
          id: 1,
          path: '',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 2,
          path: ':page',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 99,
          path: ':id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'location-expanded',
      children: [
        {
          id: 99,
          path: ':id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'station-management',
      children: [
        {
          id: 1,
          path: '',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 2,
          path: ':page',
          element: <LocationManagement />,
          permission: ['cpoChargingStationView', 'partnerChargingStationView'],
        },
        {
          id: 99,
          path: ':id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'station-expanded',
      children: [
        {
          id: 99,
          path: ':id/:tab',
          element: <LocationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'schedule-management',
      children: [
        {
          id: 1,
          path: '',
          element: <ScheduleMaintenance />,
          permission: [],
        },
        {
          id: 2,
          path: 'list',
          element: <ScheduleMaintenance />,
          permission: [],
        },
      ],
    },
    {
      path: 'charger-management',
      children: [
        {
          id: 100,
          path: ':id/:tab',
          element: <DeviceManagement />,
          permission: [],
        },
      ],
    },
    {
      path: 'charger-expanded',
      children: [
        {
          id: 101,
          path: ':id/:tab',
          element: <DeviceManagement />,
          permission: [],
        },
      ],
    },
    {
      path: 'ocpi',
      children: [
        {
          id: 1,
          path: '',
          element: <OCPIManagement />,
          permission: [],
        },
        {
          id: 2,
          path: 'connections/:id',
          element: <ConnectionManagement />,
          permission: [],
        },
        {
          id: 3,
          path: 'operations/:roleId',
          element: <OCPIRoleOperations />,
          permission: [],
        },
      ],
    },
    {
      path: 'operations/ocpp-logs',
      children: [
        {
          id: 101,
          path: '',
          element: <OCPPLogs />,
          permission: [],
        },
      ],
    },
    {
      path: 'settings',
      children: [
        {
          id: 1,
          path: '',
          element: <Settings />,
          permission: [],
        },
      ],
    },
  ];

  useEffect(() => {
    if (notificationCode === 401) {
      navigate('/login/credentials');
    }
  }, [notificationCode]);
  return (
    <>
      <Routes>
        {/* <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgetPassword />} />
        <Route path="/otp-verification" element={<OTPVerification />} />
        <Route path="/new-password" element={<NewPasswordChange />} /> */}
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* <Route path="/" element={<Navigate to="/configuration" />} />
        <Route path="configuration/paymentGateway" element={<PaymentGateway />} /> */}
        <Route
          path="/ev-admin/create-custom-dashboard"
          element={<AuthGuard permission={[]} Component={<CreateNewDashboard />} />}
        />
        <Route
          path="/ev-admin/edit-custom-dashboard/:id"
          element={<AuthGuard permission={[]} Component={<CreateNewDashboard />} />}
        />
        <Route element={<AuthLayout />}>
          <Route path="login" element={<Navigate to="/login/credentials" />} />
          <Route path="login/:page" element={<LoginV3 />} />
        </Route>
        <Route path="/ev-admin" element={<WrapperRoute />}>
          {routes.map((route) => (
            <Route key={route.path} path={route.path}>
              {route.children.map((subRoute) => (
                <Route
                  key={subRoute?.id}
                  path={subRoute?.path}
                  element={
                    <AuthGuard permission={subRoute?.permission} Component={subRoute?.element} />
                  }
                />
              ))}
            </Route>
          ))}
        </Route>
        {/* Route to manage ocpp messages */}
        <Route
          path="/ocpp-message"
          element={<AuthGuard permission={[]} Component={<OcppWrapperRoute />} />}>
          <Route path="change-configuration" element={<ChangeConfiguration />} />
          <Route path="get-configuration" element={<GetConfiguration />} />
          <Route path="clear-cache" element={<ClearCache />} />
          <Route path="get-diagnostics" element={<GetDiagnostics />} />
          <Route path="remote-start-transaction" element={<RemoteStartTransaction />} />
          <Route path="remote-stop-transaction" element={<RemoteStopTransaction />} />
          <Route path="unlock-connector" element={<UnlockConnector />} />
          <Route path="reserve-now" element={<ReserveNow />} />
          <Route path="cancel-reservation" element={<CancelReservation />} />
          <Route path="reset" element={<Reset />} />
          <Route path="get-local-list-version" element={<GetLocalVersionList />} />
          <Route path="trigger-message" element={<TriggerMessage />} />
        </Route>
      </Routes>
      <ToastMessage
        open={notificationStatus}
        type={notificationType}
        message={notificationMessage || 'Something went wrong!'}
        onClose={() =>
          dispatch(
            setNotificationMessage({
              status: false,
              message: '',
              type: 'error',
              code: 0,
            }),
          )
        }
        onCloseTimer={3000}
      />
    </>
  );
}

export default Router;
